import { ArrowSquareOut } from "@phosphor-icons/react";
import { type ReactNode, useState } from "react";
import { Link } from "react-router-dom";

import {
  MobileHeading,
  NavTabsContentContainer,
} from "features/layout/components";

import EmptyPlaceholder from "common/components/EmptyPlaceholder";
import ErrorPlaceholder from "common/components/ErrorPlaceholder";
import Loader from "common/components/Loader";
import RenderIf from "common/components/RenderIf";
import SearchInput from "common/components/SearchInput";
import { Button } from "common/components/ui/Button";
import useWorkspaceProxies from "common/datahooks/useWorkspaceProxies";
import { getWhitelabelCustomization } from "common/helpers/utils";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import { ACCOUNTS_TAB_ITEMS } from "../../constants";
import AddProxyButton from "./AddProxyButton";
import ProxiesDesktopTable from "./DesktopLayout/ProxiesDesktopTable";
import MobileList from "./MobileLayout/MobileList";

import EmptyStateImage from "assets/images/empty-placeholders/proxies.png";
import NoResultsImage from "assets/images/empty-placeholders/result-not-found.png";

export default function Proxies() {
  const isDesktop = useTwBreakpoint("lg");
  const [searchTerm, setSearchTerm] = useState("");

  const {
    isWorkspaceProxiesError,
    workspaceProxies,
    refetchWorkspaceProxies,
    isLoadingWorkspaceProxies,
  } = useWorkspaceProxies();

  let mainComponent: ReactNode;
  const searchInputComponent = (
    <SearchInput
      placeholder="Search proxies"
      value={searchTerm}
      className="lg:max-w-72"
      variant={isDesktop ? "sm" : "lg"}
      onChange={(e) => setSearchTerm(e.target.value)}
      onClear={() => setSearchTerm("")}
    />
  );

  if (isWorkspaceProxiesError) {
    mainComponent = (
      <ErrorPlaceholder
        onRetry={refetchWorkspaceProxies}
        errorMessage="Could not display proxies"
      />
    );
  } else if (isLoadingWorkspaceProxies) {
    mainComponent = <Loader />;
  } else if (!workspaceProxies?.length) {
    mainComponent = (
      <EmptyPlaceholder
        imageSrc={EmptyStateImage}
        title="No custom proxies found"
        subtitle="You're currently using our secure proxy network. You can add your own proxies if you need a custom setup"
        actionComponent={<AddProxyButton size="md" />}
      />
    );
  } else {
    const filteredProxies = workspaceProxies.filter(({ name }) =>
      name.toLowerCase().includes(searchTerm.toLowerCase()),
    );

    const Component = isDesktop ? ProxiesDesktopTable : MobileList;

    mainComponent = filteredProxies.length ? (
      <>
        {isDesktop && searchInputComponent}
        <Component proxies={filteredProxies} />
      </>
    ) : (
      <EmptyPlaceholder
        title="No custom proxies found"
        subtitle="There were no results that match your criteria"
        imageSrc={NoResultsImage}
        actionComponent={
          <Button onClick={() => setSearchTerm("")}>Clear</Button>
        }
      />
    );
  }

  const actionButtons = (
    <RenderIf condition={!getWhitelabelCustomization()}>
      <Button
        variant="secondary-black"
        leftIcon={<ArrowSquareOut />}
        asChild
        className="max-lg:order-2 max-lg:ml-auto"
        size={isDesktop ? "sm" : "md"}
      >
        <Link to="https://docs.aimfox.com/" target="_blank" rel="noreferrer">
          Documentation
        </Link>
      </Button>
      <RenderIf condition={!!workspaceProxies?.length}>
        <AddProxyButton size={isDesktop ? "sm" : "md"} />
      </RenderIf>
    </RenderIf>
  );

  return (
    <NavTabsContentContainer
      desktopActionComponent={actionButtons}
      tabItems={ACCOUNTS_TAB_ITEMS}
    >
      <MobileHeading
        actionComponent={actionButtons}
        searchComponent={searchInputComponent}
      />
      {mainComponent}
    </NavTabsContentContainer>
  );
}
